import { Grid } from "@mui/material";
import { AutosaveFormTextField } from "components/Forms/AutosaveFormTextField";
import { InfoBox } from "components/InfoBox";
import { useShowWasteStateRegistration } from "hooks/useWasteStateRegistration";
import { useCallback } from "react";
import { formatAmountInPounds } from "util/formatNumber";

import { WASTE_AMOUNT_TOOLTIP } from "../../../wasteStreamAmount";
import { formatWasteStreamProfileNumber } from "../formatWasteStreamProfileNumber";
import { prettyPrintWasteStreamLabel } from "../prettyPrintWasteClassification";
import { prettyPrintWasteStreamRegistrationStatus } from "../prettyPrintWasteStreamRegistrationStatus";
import { useWasteStream, useUpdateWasteStream } from "./api";
import { ManifestType } from "./WasteStreamDetailsModalV1";

interface WasteStreamInfoSectionProps {
  wasteStreamId: string;
  manifestType: ManifestType;
  facilityState?: string;
}
const getGridSize = (relativeSize: "sm" | "md" | "lg") => {
  switch (relativeSize) {
    case "sm":
      return { xs: 6, sm: 4, xl: 12 / 5 };
    case "md":
      return { xs: 6, sm: 4, xl: 3 };
    case "lg":
      return { xs: 12, md: 12, xl: 6 };
  }
};

export const WasteStreamInfoSection = ({
  manifestType,
  wasteStreamId,
  facilityState,
}: WasteStreamInfoSectionProps) => {
  const { data: wasteStreamData } = useWasteStream(wasteStreamId);
  const [updateWasteStream] = useUpdateWasteStream();
  const wasteStream = wasteStreamData?.wasteStream;
  const showStateRegistration = useShowWasteStateRegistration(facilityState);

  const handleSaveComment = useCallback(
    async (value: string) => {
      await updateWasteStream({
        variables: {
          input: { id: wasteStreamId, comment: value },
        },
      });
    },
    [wasteStreamId, updateWasteStream]
  );

  return (
    <>
      {/* High-level Waste Stream Info */}
      <Grid container spacing={2} sx={{ alignItems: "flex-start" }}>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={formatWasteStreamProfileNumber({
              eManifestProfileNumber: wasteStream?.eManifestProfileNumber,
              vendorProfileNumber: wasteStream?.vendorProfileNumber,
            })}
            label="Profile #"
          />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={wasteStream?.federalWasteCodes?.join(", ") || ""}
            label="Federal Waste Code"
          />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={wasteStream?.stateWasteCodes?.join(", ") || ""}
            label={`${facilityState} Waste Code`}
          />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={prettyPrintWasteStreamLabel(wasteStream?.classification, wasteStream?.wasteKind)}
            label="Classification"
          />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={wasteStream?.willNotReport ? "Not Reportable" : "Reportable"}
            label="Reportable Status"
          />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={(wasteStream?.managementMethodCodes ?? []).join(", ")}
            label="Management Method"
          />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox text={wasteStream?.formCode || ""} label="Form Code" />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox text={wasteStream?.sourceCode || ""} label="Source Code" />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={wasteStream?.wasteMinimizationCode || ""}
            label="Waste Minimization Code"
          />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={
              manifestType === "vendor"
                ? wasteStream?.vendorDotDescription || ""
                : wasteStream?.eManifestDotDescription || ""
            }
            label="DOT Description"
          />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={
              manifestType === "vendor"
                ? formatAmountInPounds(wasteStream?.vendorAmount)
                : formatAmountInPounds(wasteStream?.eManifestAmount)
            }
            label={manifestType === "vendor" ? "Amount (vendor)" : "Amount (e‑Manifest)"}
          />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={formatAmountInPounds(wasteStream?.effectiveAmount)}
            label="Amount"
            infoTooltip={WASTE_AMOUNT_TOOLTIP}
          />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={
              manifestType === "vendor"
                ? `${wasteStream?.vendorManifestCount || 0}`
                : `${wasteStream?.eManifestCount || 0}`
            }
            label="Manifests Count"
          />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={
              manifestType === "vendor"
                ? `${wasteStream?.vendorManifestLineItemCount || 0}`
                : `${wasteStream?.eManifestLineItemCount || 0}`
            }
            label="Manifests Line Item Count"
          />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={
              wasteStream?.lastShipmentDateAllTime
                ? new Date(wasteStream.lastShipmentDateAllTime).toLocaleDateString(undefined, {
                    timeZone: "UTC",
                  })
                : ""
            }
            label="Last Shipment Date (All Time)"
          />
        </Grid>
        {showStateRegistration && (
          <>
            <Grid size={getGridSize("sm")}>
              <InfoBox
                text={wasteStream?.registration?.stateRegisteredId ?? ""}
                label="State Registered ID"
              />
            </Grid>
            <Grid size={getGridSize("sm")}>
              <InfoBox
                text={prettyPrintWasteStreamRegistrationStatus(wasteStream?.registration?.status)}
                label="Registration Status"
              />
            </Grid>
            <Grid size={getGridSize("sm")}>
              <InfoBox
                text={
                  wasteStream?.registration?.registeredAt
                    ? new Date(wasteStream.registration.registeredAt).toLocaleDateString(
                        undefined,
                        {
                          timeZone: "UTC",
                        }
                      )
                    : ""
                }
                label="Registered At"
              />
            </Grid>
          </>
        )}
        <Grid size={getGridSize("lg")}>
          <AutosaveFormTextField
            onSave={handleSaveComment}
            label="Comment"
            defaultValue={wasteStream?.comment ?? ""}
            multiline={true}
          />
        </Grid>
      </Grid>
    </>
  );
};
